import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Autocomplete,
  Alert,
} from "@mui/material";

import { accessoriesService } from "../service/AccessoriesService";
import api from "../service/api";

const AccessoriesPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔥 FILTER STATE (UPDATED)
  const [filters, setFilters] = useState({
    itemType: "",
    giftName: "", // 🎁 only meaningful when itemType === "Gift"
    startDate: "",
    endDate: "",
    month: "",
    filterYear: "",
  });

  // 🔥 POPUP STATE
  const [open, setOpen] = useState(false);

  // ✅ SELL ACCESSORY POPUP STATE
  const [sellOpen, setSellOpen] = useState(false);
  const [sellSubmitting, setSellSubmitting] = useState(false);
  const [sellError, setSellError] = useState(""); // 🔧 NEW — surfaces why Sell failed instead of failing silently

  // 🔥 FORM STATE (UPDATED)
  const [form, setForm] = useState({
    itemType: "",
    giftName: "",
    price: "",
    quantity: "",
    year: new Date().getFullYear(),
    date: "",
  });

  // ✅ SELL ACCESSORY FORM STATE
  const [sellForm, setSellForm] = useState({
    customerName: "",
    location: "",
    phone: "",
    itemType: "",
    giftName: "",
    quantity: "",
    sellingPrice: "",
    date: "",
  });

  const itemOptions = ["Helmet", "Seat Cover", "Bike Cover", "Gift"];

  // ✅ fixed location list — keeps Sell Accessory in sync with the rest
  // of the system so location-wise reports don't fragment on free-text typos
  const locationOptions = ["Khejuri", "Chaitanyapur", "Nandigram"];

  // 🎁 NEW — known gift names, fetched once, powers the autocompletes below
  const [giftOptions, setGiftOptions] = useState([]);

  useEffect(() => {
    let active = true;
    api.get("/accessories")
      .then((res) => {
        if (!active) return;
        const rows = res.data?.data || res.data || [];
        const names = rows
          .filter((a) => a.itemType === "Gift" && a.giftName)
          .map((a) => a.giftName);
        setGiftOptions([...new Set(names)]);
      })
      .catch(() => { /* ignore — free text still works without suggestions */ });
    return () => { active = false; };
  }, []);

  // 🔄 FILTER CHANGE
  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  // 🔄 FORM CHANGE
  const handleFormChange = (e) => {
    const { name, value } = e.target;

    // 🔥 AUTO YEAR FROM DATE
    if (name === "date") {
      const year = new Date(value).getFullYear();
      setForm({ ...form, date: value, year });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // ✅ SELL FORM CHANGE
  const handleSellFormChange = (e) => {
    const { name, value } = e.target;
    setSellForm({ ...sellForm, [name]: value });
  };

  // 🚀 FETCH DATA
  const fetchData = async (customFilters = filters) => {
  try {
    setLoading(true);

    const res = await accessoriesService.getCategoryReport(customFilters);

    console.log("API DATA:", res);

    setData(res.data || []);
  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  fetchData({}); // 🔥 IMPORTANT: no filters
}, []);

  // 🔍 SEARCH
const handleSearch = () => {
  fetchData(filters);
};
  // 🔄 RESET
const handleReset = () => {
  const emptyFilters = {
    itemType: "",
    giftName: "",
    startDate: "",
    endDate: "",
    month: "",
    filterYear: "",
  };

  setFilters(emptyFilters);

  // 🔥 CALL WITHOUT FILTERS
  fetchData({});
};

  // ➕ CREATE
  const handleCreate = async () => {
    if (form.itemType === "Gift" && !(form.giftName || "").trim()) {
      return;
    }

    try {
      await accessoriesService.createAccessory(form);
      setOpen(false);
      setForm({
        itemType: "",
        giftName: "",
        price: "",
        quantity: "",
        year: new Date().getFullYear(),
        date: "",
      });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  // ✅ SELL INDIVIDUAL ACCESSORY
  const handleSell = async () => {
    setSellError(""); // 🔧 clear any previous error before re-validating

    if (
      !sellForm.customerName ||
      !sellForm.location ||
      !sellForm.phone ||
      !sellForm.itemType ||
      !sellForm.quantity ||
      !sellForm.sellingPrice ||
      (sellForm.itemType === "Gift" && !(sellForm.giftName || "").trim()) // 🔧 optional chaining guard
    ) {
      setSellError("Please fill all required fields before selling."); // 🔧 NEW — was a silent `return`
      return;
    }

    try {
      setSellSubmitting(true);
      await accessoriesService.sellAccessory(sellForm);
      setSellOpen(false);
      setSellForm({
        customerName: "",
        location: "",
        phone: "",
        itemType: "",
        giftName: "",
        quantity: "",
        sellingPrice: "",
        date: "",
      });
      fetchData();
    } catch (err) {
      // 🔧 NEW — surface the actual backend error instead of only console.error
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while selling. Please try again.";
      setSellError(message);
      console.error(err);
    } finally {
      setSellSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Accessories Management
      </Typography>

      {/* 🔥 FILTER SECTION */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Item Type"
              name="itemType"
              fullWidth
              value={filters.itemType}
              onChange={handleChange}
            >
              {itemOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* 🎁 only shown when filtering by Gift, narrows to one gift name */}
          {filters.itemType === "Gift" && (
            <Grid item xs={12} md={3}>
              <TextField
                label="Gift Name"
                name="giftName"
                fullWidth
                value={filters.giftName}
                onChange={handleChange}
              />
            </Grid>
          )}

          <Grid item xs={12} md={3}>
            <TextField
              label="Start Date"
              type="date"
              name="startDate"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.startDate}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="End Date"
              type="date"
              name="endDate"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.endDate}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Month (1-12)"
              name="month"
              type="number"
              fullWidth
              value={filters.month}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Year"
              name="filterYear"
              fullWidth
              value={filters.filterYear}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <Button variant="contained" fullWidth onClick={handleSearch}>
              Search
            </Button>
          </Grid>

          <Grid item xs={6} md={3}>
            <Button variant="outlined" fullWidth onClick={handleReset}>
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ➕ CREATE BUTTON */}
      <Button
        variant="contained"
        color="success"
        onClick={() => setOpen(true)}
        sx={{ mb: 2, mr: 2 }}
      >
        + Create Accessories
      </Button>

      {/* ✅ SELL ACCESSORY BUTTON */}
      <Button
        variant="contained"
        color="primary"
        onClick={() => { setSellError(""); setSellOpen(true); }}
        sx={{ mb: 2 }}
      >
        + Sell Accessory
      </Button>

      {/* 🔥 TABLE */}
      {loading ? (
        <CircularProgress />
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Total</TableCell>
              </TableRow>
            </TableHead>

          <TableBody>
  {data.length === 0 ? (
    <TableRow>
      <TableCell colSpan={5} align="center">
        No Data Found ❌
      </TableCell>
    </TableRow>
  ) : (
    data.map((row, index) => (
      <TableRow key={index}>
        {/* Item Type — shows the gift name alongside "Gift" when present */}
        <TableCell>
          {row?.itemType === "Gift" && row?.giftName
            ? `Gift: ${row.giftName}`
            : row?.itemType || "-"}
        </TableCell>

        {/* Date */}
        <TableCell>
          {row?.date
            ? new Date(row.date).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "-"}
        </TableCell>

        {/* Quantity */}
        <TableCell>{row?.totalQuantity ?? 0}</TableCell>

        {/* Avg Price */}
        <TableCell>
          {row?.avgPrice !== undefined
            ? Number(row.avgPrice).toFixed(2)
            : "0.00"}
        </TableCell>

        {/* Total Amount */}
        <TableCell>{row?.totalAmount ?? 0}</TableCell>
      </TableRow>
    ))
  )}
</TableBody>
          </Table>
        </Paper>
      )}

      {/* 🔥 CREATE DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Create Accessories</DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                label="Item Type"
                name="itemType"
                fullWidth
                value={form.itemType}
                onChange={handleFormChange}
              >
                {itemOptions.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* 🎁 CHANGED: now an autocomplete that suggests known gift names, freeSolo still allows typing a new one */}
            {form.itemType === "Gift" && (
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  fullWidth
                  options={giftOptions}
                  value={form.giftName}
                  onInputChange={(_, newValue) => {
                    setForm((p) => ({ ...p, giftName: newValue }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Gift Name" required fullWidth />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Price"
                name="price"
                type="number"
                fullWidth
                value={form.price}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Quantity"
                name="quantity"
                type="number"
                fullWidth
                value={form.quantity}
                onChange={handleFormChange}
              />
            </Grid>

            {/* 🔥 DATE FIELD (CALENDAR) */}
            <Grid item xs={12}>
              <TextField
                label="Date"
                name="date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.date}
                onChange={handleFormChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Year"
                name="year"
                fullWidth
                value={form.year}
                onChange={handleFormChange}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handleCreate}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ SELL ACCESSORY DIALOG */}
      <Dialog open={sellOpen} onClose={() => setSellOpen(false)}>
        <DialogTitle>Sell Accessory</DialogTitle>

        <DialogContent>
          {/* 🔧 NEW — surfaces validation/backend errors instead of failing silently */}
          {sellError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {sellError}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: sellError ? 0 : 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Customer Name"
                name="customerName"
                fullWidth
                value={sellForm.customerName}
                onChange={handleSellFormChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                required
                label="Location"
                name="location"
                fullWidth
                value={sellForm.location}
                onChange={handleSellFormChange}
              >
                {locationOptions.map((loc) => (
                  <MenuItem key={loc} value={loc}>
                    {loc}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Phone Number"
                name="phone"
                fullWidth
                value={sellForm.phone}
                onChange={handleSellFormChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                label="Item Type"
                name="itemType"
                fullWidth
                value={sellForm.itemType}
                onChange={handleSellFormChange}
              >
                {itemOptions.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* 🎁 CHANGED: now an autocomplete that suggests known gift names, freeSolo still allows typing a new one */}
            {sellForm.itemType === "Gift" && (
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  fullWidth
                  options={giftOptions}
                  value={sellForm.giftName}
                  onInputChange={(_, newValue) => {
                    setSellForm((p) => ({ ...p, giftName: newValue }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Gift Name" required fullWidth />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Quantity"
                name="quantity"
                type="number"
                fullWidth
                value={sellForm.quantity}
                onChange={handleSellFormChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Selling Price"
                name="sellingPrice"
                type="number"
                required
                fullWidth
                value={sellForm.sellingPrice}
                onChange={handleSellFormChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Date"
                name="date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={sellForm.date}
                onChange={handleSellFormChange}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSellOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={handleSell}
            disabled={sellSubmitting}
          >
            {sellSubmitting ? "Selling..." : "Sell"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessoriesPage;